import { 
  collection, 
  doc, 
  addDoc, 
  getDoc,
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Database service with collections and operations
const DatabaseService = {
  // Items collection operations
  items: {
    // Get all items for a user
    getAll: async (userId) => {
      try {
        const q = query(
          collection(db, 'items'),
          where('userId', '==', userId)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error getting items:', error);
        throw error;
      }
    },

    // Get a single item by ID
    get: async (id) => {
      try {
        const docRef = doc(db, 'items', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          return {
            id: docSnap.id,
            ...docSnap.data()
          };
        } else {
          return null;
        }
      } catch (error) {
        console.error('Error getting item:', error);
        throw error;
      }
    },

    // Add a new item
    add: async (itemData) => {
      try {
        const item = {
          ...itemData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'items'), item);
        return docRef.id;
      } catch (error) {
        console.error('Error adding item:', error);
        throw error;
      }
    },

    // Update an existing item
    update: async (id, itemData) => {
      try {
        const itemRef = doc(db, 'items', id);
        await updateDoc(itemRef, {
          ...itemData,
          updatedAt: serverTimestamp()
        });
        return true;
      } catch (error) {
        console.error('Error updating item:', error);
        throw error;
      }
    },

    // Delete an item
    delete: async (id) => {
      try {
        const itemRef = doc(db, 'items', id);
        await deleteDoc(itemRef);
        return true;
      } catch (error) {
        console.error('Error deleting item:', error);
        throw error;
      }
    }
  },

  // Waste logs collection operations
  wasteLogs: {
    // Get all waste logs for a user
    getAll: async (userId) => {
      try {
        const q = query(
          collection(db, 'wasteLogs'),
          where('userId', '==', userId),
          orderBy('date', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const logs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`Loaded ${logs.length} waste logs for user ${userId}`);
        return logs;
      } catch (error) {
        console.error('Error getting waste logs:', error);
        if (error.code === 'failed-precondition') {
          console.warn('Index not found. Please create the required index:', error);
          console.warn('Attempting to fetch without orderBy...');
          // Try without orderBy as fallback
          try {
            const q = query(
              collection(db, 'wasteLogs'),
              where('userId', '==', userId)
            );
            const querySnapshot = await getDocs(q);
            const logs = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              // Sort by date manually
              const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
              const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
              return dateB - dateA;
            });
            console.log(`Loaded ${logs.length} waste logs (without index)`);
            return logs;
          } catch (fallbackError) {
            console.error('Fallback query also failed:', fallbackError);
            return [];
          }
        }
        throw error;
      }
    },

    // Get all waste logs for a user for a specific date
    getAllForDate: async (userId, date) => {
      try {
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        const q = query(
          collection(db, 'wasteLogs'),
          where('userId', '==', userId),
          where('date', '>=', start),
          where('date', '<', end)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error getting waste logs for date:', error);
        return [];
      }
    },

    // Add a new waste log
    add: async (wasteData) => {
      try {
        // Filter out undefined values (Firestore doesn't accept undefined)
        const cleanedData = Object.fromEntries(
          Object.entries(wasteData).filter(([, value]) => value !== undefined)
        );
        
        // Convert date to Firestore Timestamp if it's a Date object
        let dateValue = cleanedData.date;
        if (dateValue instanceof Date) {
          dateValue = Timestamp.fromDate(dateValue);
        } else if (!dateValue) {
          dateValue = serverTimestamp();
        }
        
        const waste = {
          ...cleanedData,
          date: dateValue,
          createdAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'wasteLogs'), waste);
        console.log('Waste log added successfully:', docRef.id);
        return docRef.id;
      } catch (error) {
        console.error('Error adding waste log:', error);
        throw error;
      }
    }
  },

  // Consumption logs collection operations
  consumption: {
    // Get all consumption logs for a user
    getAll: async (userId) => {
      try {
        const q = query(
          collection(db, 'consumption'),
          where('userId', '==', userId),
          orderBy('date', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error getting consumption logs:', error);
        if (error.code === 'failed-precondition') {
          console.warn('Index not found. Please create the required index:', error);
          // Try without orderBy as fallback
          try {
            const q = query(
              collection(db, 'consumption'),
              where('userId', '==', userId)
            );
            const querySnapshot = await getDocs(q);
            const logs = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
              const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
              return dateB - dateA;
            });
            return logs;
          } catch (fallbackError) {
            console.error('Fallback query also failed:', fallbackError);
            return [];
          }
        }
        if (error.code === 'permission-denied') {
          console.warn('Permission denied for consumption logs. Returning empty array.');
          return [];
        }
        // Return empty array instead of throwing to prevent app crashes
        return [];
      }
    },

    // Add a new consumption log
    add: async (consumptionData) => {
      try {
        const cleanedData = Object.fromEntries(
          Object.entries(consumptionData).filter(([, value]) => value !== undefined)
        );

        let dateValue = cleanedData.date;
        if (dateValue instanceof Date) {
          dateValue = Timestamp.fromDate(dateValue);
        } else if (!dateValue) {
          dateValue = serverTimestamp();
        }

        const consumption = {
          ...cleanedData,
          date: dateValue,
          createdAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'consumption'), consumption);
        return docRef.id;
      } catch (error) {
        console.error('Error adding consumption log:', error);
        throw error;
      }
    }
  },

  // User profiles collection operations
  userProfiles: {
    // Get a user profile
    get: async (userId) => {
      try {
        const docRef = doc(db, 'userProfiles', userId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          return {
            id: docSnap.id,
            ...docSnap.data()
          };
        } else {
          return null;
        }
      } catch (error) {
        console.error('Error getting user profile:', error);
        throw error;
      }
    },

    // Create or update a user profile
    update: async (userId, profileData) => {
      try {
        const profileRef = doc(db, 'userProfiles', userId);
        await updateDoc(profileRef, {
          ...profileData,
          updatedAt: serverTimestamp()
        });
        return true;
      } catch (error) {
        console.error('Error updating user profile:', error);
        throw error;
      }
    }
  }
};

export default DatabaseService;

export const createRequiredIndexes = async () => {
  try {
    // Create index for wasteLogs collection
    await db.collection('wasteLogs').createIndex({
      fields: [
        { fieldPath: 'userId', order: 'ASCENDING' },
        { fieldPath: 'date', order: 'DESCENDING' }
      ]
    });

    // Create index for consumption collection
    await db.collection('consumption').createIndex({
      fields: [
        { fieldPath: 'userId', order: 'ASCENDING' },
        { fieldPath: 'date', order: 'DESCENDING' }
      ]
    });

    console.log('Required indexes created successfully');
  } catch (error) {
    console.error('Error creating indexes:', error);
  }
};

export const getAll = async (userId) => {
  try {
    const snapshot = await db.collection('wasteLogs')
      .where('userId', '==', userId)
      .orderBy('date', 'desc')
      .get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    if (error.code === 'failed-precondition') {
      console.error('Index not found. Please create the required index:', error);
      // You can show a user-friendly message here
      return [];
    }
    throw error;
  }
};

export const getAllConsumption = async (userId) => {
  try {
    const snapshot = await db.collection('consumption')
      .where('userId', '==', userId)
      .orderBy('date', 'desc')
      .get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    if (error.code === 'failed-precondition') {
      console.error('Index not found. Please create the required index:', error);
      // You can show a user-friendly message here
      return [];
    }
    throw error;
  }
}; 